<script lang="ts" setup>
import ReadOnlyView from '@/vue/molecules/read-only/read-only-view.vue';
import {computed, onMounted, ref} from 'vue';
import type {QuasarOption} from '@/ts/types/component/select-multichoice';
import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';
import {type Session, SessionState, type Intervention} from '@/ts/types/dto/interventions-dto';

const showOnlyMyContent = ref<boolean>(false);

const props = defineProps<{
  intervention: Intervention;
}>();

const completedSessions = computed<Session[]>(() => props.intervention.sessions
  .filter(sess => sess.state === SessionState.COMPLETED && sess.completedDate));

const options = computed<QuasarOption<UuidDTO>[]>(() => completedSessions.value.map(sess => ({label: sess.name, value: sess.id})));

const selectedOption = ref<QuasarOption<UuidDTO> | null>(null);
const selectedSession = computed(() => selectedOption.value 
  ? completedSessions.value.find(sess => sess.id === selectedOption.value!.value) : null);

onMounted(() => {
  if (completedSessions.value?.[0]) {
    [selectedOption.value] = options.value;
  }
});
</script>
<template>
  <div class="recap">
    <div class="recap-header">
      <div v-if="completedSessions.length === 0">
        {{ $t('no_current_sessions') }}
      </div>
      <template v-else>
        <q-select
          v-model="selectedOption"
          :options="options"
          :label="$t('session')"
          class="session-select"
        />
        <q-checkbox
          v-show="$q.screen.gt.sm"
          v-model="showOnlyMyContent"
          :label="$t('show_only_my_answers')"
          class="show-only-checkbox"
        />
      </template>
    </div>
    <read-only-view
      v-if="selectedSession"
      :session="selectedSession"
      :primary-feelings="intervention!.primaryFeelings"
      :thinking-traps="intervention!.thinkingTraps"
      :show-only-my-content="showOnlyMyContent"
    />
  </div>
</template>
<style lang="scss" scoped>
.recap {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-3);

  @include screen-md {
    gap: var(--Spacing-spacing-7);
  }
}

.recap-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--Spacing-spacing-3);
  flex-wrap: wrap;

  @include screen-md {
    flex-wrap: nowrap;
  }
}

.session-select {
  flex: 1;
  min-width: 0;

  :deep(.q-field__native) {
    white-space: normal;
    word-wrap: break-word;
    overflow-wrap: break-word;
    min-height: 24px;
  }

  :deep(.q-field__control) {
    min-height: 56px;
    height: auto;
  }

  :deep(.q-field__native span) {
    white-space: normal;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
}

.show-only-checkbox {
  flex-shrink: 0;
}
</style>
